import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import { tokenStore } from '@/api/tokenStore';
import { ApiError, toApiError } from '@/api/errors';
import type { ApiErrorBody } from '@/types/api';

/**
 * The single Axios instance for the whole portal. Feature API modules build on
 * this; components never import axios directly.
 * - Request interceptor injects the in-memory bearer token.
 * - Response interceptor normalises errors and performs a single-flight token
 *   refresh on 401, then retries the original request once.
 */
export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true, // send the httpOnly refresh cookie
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = config.method?.toUpperCase();
  if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && config.headers.has('Idempotency-Key') === false) {
    config.headers.set('Idempotency-Key', globalThis.crypto.randomUUID());
  }
  const token = tokenStore.get();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// --- Session refresh -------------------------------------------------------
// identity rotates refresh tokens and revokes the whole session if one is ever
// presented twice, so refreshes must never overlap: one in-flight refresh per
// tab (single-flight) and one across tabs (Web Locks). The refresh token itself
// lives in an HttpOnly cookie managed by the gateway; JS never sees it.
type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean; _skipAuthRefresh?: boolean };

/** Auth endpoints never trigger the 401 → refresh → retry cycle. */
const AUTH_PATHS = /\/auth\/(refresh|login|logout|logout-all|mfa|step-up|password-reset)/;
/** A wrong second-factor code is an answer, not an expired session: never refresh and resend it. */
const WRONG_CODE = 'AUTH_MFA_INVALID';

/** A refresh failure that proves the session is gone (vs. a transient error). */
export function isDefinitiveAuthFailure(e: unknown): boolean {
  return e instanceof ApiError && [400, 401, 403].includes(e.status);
}

let inflightRefresh: Promise<string> | null = null;

async function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  return locks?.request ? locks.request('nh-auth-refresh', fn) : fn();
}

/** Rotate the session and return a fresh access token (shared by every caller). */
export function refreshAccessToken(): Promise<string> {
  if (!inflightRefresh) {
    inflightRefresh = withCrossTabLock(async () => {
      const { data } = await apiClient.post<{ accessToken: string }>('/auth/refresh', {}, { _skipAuthRefresh: true } as RetriableConfig);
      tokenStore.set(data.accessToken);
      return data.accessToken;
    }).finally(() => { inflightRefresh = null; });
  }
  return inflightRefresh;
}

/** Set by AuthContext: called when the session is definitively lost. */
let onAuthFailure: (() => void) | null = null;

export function registerAuthHandlers(handlers: { onFailure: () => void }): void {
  onAuthFailure = handlers.onFailure;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const isAuthCall = !!original && (original._skipAuthRefresh || AUTH_PATHS.test(original.url ?? '')
      || error.response?.data?.code === WRONG_CODE);

    // One transparent refresh + retry on 401, never for auth endpoints themselves.
    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        const newToken = await refreshAccessToken();
        original.headers.set('Authorization', `Bearer ${newToken}`);
        return apiClient(original);
      } catch (refreshError) {
        // Only a definitive refusal ends the session; a transient failure (network,
        // 5xx, 429) just fails this request so the user is not signed out by a blip.
        if (isDefinitiveAuthFailure(refreshError)) onAuthFailure?.();
      }
    }

    return Promise.reject(toApiError(error));
  },
);
